import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { SelectionService, SelectedProduct, SelectedBenefit } from '../../core/services/selection.service';
import { ProfileService } from '../../shared/profiles/services/profile.service';
import { CompareSbsService } from '../../shared/comparison/services/compare-sbs.service';
import { CompareNavigationService } from '../../core/services/compare-navigation.service';
import { DisclosureStateService } from '../../features/disclosures/services/disclosure-state.service';

@Component({
  selector: 'app-selection-tray',
  standalone: true,
  // Not OnPush: this reacts to DisclosureStateService.step(), which is set
  // from a component in the disclosures feature — a lazy-loaded route, not
  // an ancestor of this shell component. App-shell components here have
  // shown stale renders before when driven by signals set from lazy-loaded
  // routes; skip that risk rather than re-litigate it.
  imports: [CommonModule],
  templateUrl: './selection-tray.component.html',
  styleUrl: './selection-tray.component.scss'
})
export class SelectionTrayComponent {
  readonly sel = inject(SelectionService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private compareSbs = inject(CompareSbsService);
  protected compareNav = inject(CompareNavigationService);
  private disclosureState = inject(DisclosureStateService);
  collapsed = signal(true);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  /** True on the Disclosures wizard's Product Selection step specifically —
   *  the one place a dragged basket product actually has somewhere to land.
   *  Checking the route, not just the step, matters because
   *  DisclosureStateService is a root singleton: its step stays whatever it
   *  was last left at even after navigating away from /disclosures. Matched
   *  exactly (plus an optional query string) rather than by prefix, so this
   *  doesn't also fire on /disclosures/report or /disclosures/supervisor. */
  readonly onDisclosures = computed(() => this.currentUrl().split('?')[0] === '/disclosures');

  readonly onProductSelectionStep = computed(() =>
    this.onDisclosures() && this.disclosureState.step() === 2);

  /** Same rule the wizard's own Continue / step tabs apply: back from a later
   *  step always, forward from Information only once its names are valid. */
  readonly canJumpToProductSelection = computed(() =>
    this.disclosureState.step() > 2 || this.disclosureState.canProceedToStep2());

  /** The basket's "Drag" buttons. On Product Selection they point at where to
   *  drop (the boxes flash); on any other wizard step they take the user to
   *  Product Selection first, then point. The drag itself is unchanged — the
   *  whole card is still the drag source. */
  suggestDrag(): void {
    if (!this.onDisclosures()) return;
    if (!this.onProductSelectionStep()) {
      if (!this.canJumpToProductSelection()) return;
      this.disclosureState.setStep(2);
    }
    this.disclosureState.flashDropZones();
  }

  /** Basket product ids already in the disclosure report, however they got
   *  there (dragged, or picked from a card's dropdown). Same id space: a drop
   *  writes the basket product's id straight into the report's productId. */
  private readonly reportProductIds = computed(() => new Set(
    this.disclosureState.products()
      .map(p => p.productId)
      .filter((id): id is number => id !== null)));

  /** In the report already, so not draggable again — a second drop would only
   *  be rejected as a duplicate. Disclosures only: elsewhere the report's
   *  contents are irrelevant to the basket. Removing the product from the
   *  report makes it draggable again. */
  isInReport(p: SelectedProduct): boolean {
    return this.onDisclosures() && this.reportProductIds().has(p.id);
  }

  /** Basket products that can still be dragged in. */
  readonly draggableCount = computed(() =>
    this.sel.products().filter(p => !this.isInReport(p)).length);

  readonly dragButtonTip = computed(() => {
    if (this.onProductSelectionStep()) return 'Drag this card onto a product box in the report';
    return this.canJumpToProductSelection()
      ? 'Go to Product Selection to drag this into the report'
      : 'Fix the client / advisor name on Information first';
  });

  toggleCollapse() { this.collapsed.update(v => !v); }

  removeProduct(selectionId: string) { this.sel.removeProduct(selectionId); }
  removeBenefit(id: number) { this.sel.removeBenefit(id); }

  /** Drag source for dropping a basket product onto Disclosures' Product
   *  Selection step. `setData` is required for Firefox to start the drag at
   *  all; the value itself isn't read anywhere — the actual payload travels
   *  via SelectionService.draggingProduct(), not the DataTransfer. */
  onProductDragStart(ev: DragEvent, p: SelectedProduct): void {
    // draggable="false" already stops this; guard anyway for a drag that
    // starts from a child element the browser still lets through.
    if (this.isInReport(p)) { ev.preventDefault(); return; }
    ev.dataTransfer?.setData('text/plain', p.selectionId);
    this.sel.startDrag(p);
  }

  onProductDragEnd(): void {
    this.sel.endDrag();
  }

  /** Riders added from the Benefit Calculator are basket contents too — a
   *  "Clear all" that left them behind was the reason the bar could still hold
   *  selections the user believed they had just emptied. */
  clearAll() {
    this.sel.clearProducts();
    this.sel.clearBenefits();
  }

  /** Anything at all in the basket, products or riders. */
  hasSelections(): boolean {
    return this.sel.productCount() > 0 || this.sel.benefitCount() > 0;
  }

  benefitSubtitle(b: SelectedBenefit): string {
    return [b.productName, b.carrier].filter(Boolean).join(' · ');
  }

  /** A comparison needs two products; below that compareProducts() has
   *  nothing to stage, so the button says so instead of no-opping. */
  readonly canCompare = computed(() => this.sel.productCount() >= 2);

  readonly compareTip = computed(() => {
    const n = this.sel.productCount();
    if (n >= 2) return `Compare ${n} products side-by-side`;
    return n === 1
      ? 'Add one more product to compare'
      : 'Add at least 2 products to compare';
  });

  compareProducts(): void {
    const selected = this.sel.products();
    if (selected.length < 2) return;
    const items = selected.slice(0, 4);
    const riders = items.map(p => ({
      id: p.id,
      productid: p.id,
      companyName: p.carrier,
      productName: p.name,
      productType: p.productType.toUpperCase() as 'FA' | 'IA' | 'RILA' | 'VA',
      rateRow: p.rateRow,
      // Present only for products ticked on a rates grid — the Interest tab
      // shows those picks and leaves every other entry point as it was.
      rateRows: this.sel.rateGridRows(p),
    }));
    const staged = this.compareSbs.stageContext({ riders, scenario: null });
    if (!staged) return;
    this.compareNav.routeToCompare();
  }

  createDisclosure() {
    // TODO: launch disclosure wizard with selections
    console.log('Create disclosure');
  }

  openProfile(product: SelectedProduct) {
    this.profileService.open({
      productId: product.id,
      productType: product.productType,
      source: 'basket'
    });
  }

  formatSource(source: string): string {
    const map: Record<string, string> = {
      'fa-rate-grid': 'FA Rate Sheet',
      'ia-rate-grid': 'IA Rate Sheet',
      'va-rate-grid': 'VA Rate Sheet',
      'rila-rate-grid': 'RILA Rate Sheet',
      'product-search': 'Product Search',
      'benefit-search': 'Benefit Search',
      'calculator': 'Benefit Calculator',
      'product-profile': 'Product Profile'
    };
    return map[source] || source;
  }

  splitFilters(filters: string): string[] {
    if (!filters) return [];
    return filters.split(/[·,]/).map(s => s.trim()).filter(Boolean);
  }

  getBadgeClass(type: string): string {
    return 'tb-' + type;
  }
}
